import { Button, Text, TextInput, YearMonthRangePopover } from 'bench-internal-theme';
import { getISODateStringInUTC, getLastDateOfMonth, toDate } from 'date-functions';
import React, { useCallback, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { AppSidebar } from 'bk-web-app/app-sidebar';
import { ClientRulesSidebar } from 'bk-web-app/client-rules-sidebar';

import { useGeneralLedger } from './contexts';
import { VisuallyHidden } from './fast-gl/NewGeneralLedger.styles';
import SettingsSidebar from './SettingsSidebar';
import debounce from 'lodash/debounce';
import { colors } from 'bench-style-constants';

const SearchInputWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const CircleSpinner = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  margin-top: -7px; /* Half of height to perfectly center */
  width: 14px;
  height: 14px;
  border: 2px solid ${colors.dust};
  border-top: 2px solid ${colors.leaf};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const getFormattedDateString = dateString => {
  if (!dateString) {
    return '';
  }
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
};

const PageControlsLayout = styled.div`
  align-items: center;
  display: grid;
  grid-column-gap: 8px;
  grid-template-columns: max-content 180px 252px 100px max-content max-content;
  justify-content: flex-start;
  padding: 16px 32px;
  white-space: nowrap;
`;

type PageControlsProps = {
  hasQualityIssues?: boolean;
};

const PageControls = ({ hasQualityIssues }: PageControlsProps) => {
  const {
    dateRange: { endDate, startDate },
    filters: { search },
    updateDateRange,
    updateFilters,
  } = useGeneralLedger();

  const [isSearching, setIsSearching] = useState(false);

  const debouncedUpdateSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        updateFilters({ search: searchValue });
        setIsSearching(false);
      }, 500),
    [updateFilters],
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setIsSearching(Boolean(value.trim()));
      debouncedUpdateSearch(value);
    },
    [debouncedUpdateSearch],
  );
  // If there's a start date, get the year portion, otherwise get whatever year it is locally
  const defaultDisplayedYear = startDate
    ? Number(startDate.split('-')[0])
    : new Date().getFullYear();
  // If there are start or end dates, automatically select them when the date popup opens
  const defaultStartYearMonth = startDate && startDate.substr(0, 7);
  const defaultEndYearMonth = endDate && endDate.substr(0, 7);
  // Get a formatted representation of the date range to display in the text input
  const dateRangeText =
    startDate && endDate
      ? `${getFormattedDateString(startDate)} – ${getFormattedDateString(endDate)}`
      : 'No date range selected';
  const handleRangeSelect = (startYearMonth: string | null, endYearMonth: string | null) => {
    if (!startYearMonth || !endYearMonth) return;
    updateDateRange({
      endDate: getISODateStringInUTC(getLastDateOfMonth(endYearMonth)),
      startDate: getISODateStringInUTC(toDate(startYearMonth)),
    });
  };
  return (
    <PageControlsLayout>
      <Text uppercase>Date range</Text>
      <YearMonthRangePopover
        defaultDisplayedYear={defaultDisplayedYear}
        defaultStartYearMonth={defaultStartYearMonth}
        defaultEndYearMonth={defaultEndYearMonth}
        onRangeSelect={handleRangeSelect}
      >
        {showPopover => (
          <TextInput onFocus={() => showPopover(true)} styleType="ghost" value={dateRangeText} />
        )}
      </YearMonthRangePopover>
      <SearchInputWrapper>
        <TextInput
          defaultValue={search}
          onChange={handleSearchChange}
          placeholder="Search"
          id="search"
          name="search"
        />
        {isSearching && <CircleSpinner data-testid="search-spinner" />}
      </SearchInputWrapper>
      <VisuallyHidden as="label" htmlFor="search">
        Search
      </VisuallyHidden>
      <AppSidebar>
        {({ closeSidebar, openSidebar }) => (
          <Button
            size="medium"
            styleType="primary"
            onClick={() => openSidebar(<ClientRulesSidebar dismiss={closeSidebar} />)}
          >
            New Rule
          </Button>
        )}
      </AppSidebar>
      <AppSidebar>
        {({ closeSidebar, openSidebar }) => (
          <Button
            size="medium"
            styleType="secondary"
            onClick={() =>
              openSidebar(
                <SettingsSidebar dismiss={closeSidebar} hasQualityIssues={hasQualityIssues} />,
              )
            }
          >
            Settings
          </Button>
        )}
      </AppSidebar>
    </PageControlsLayout>
  );
};

PageControls.defaultProps = {
  hasQualityIssues: false,
};

// $FlowFixMe
export default React.memo(PageControls);
